import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";

export interface EmbedPreviewStep {
  title: string;
  subtitle?: string;
  description?: string;
  icon?: string;
  image?: string;
  video?: string | null;
}

export interface EmbedPreviewItem {
  name: string;
  detail?: string;
  icon?: string;
}

/**
 * Lightweight, self-contained embed preview player (matches the shared
 * reference): a single-column card with the media canvas, floating
 * Steps/Items tabs, and bottom timeline/action controls.
 *
 * Tapping the Steps/Items tab opens a selection panel over the video; the
 * selected step is then shown in the main area.
 *
 * All content is driven by @Input()s and falls back to demo data so the
 * component renders on its own (e.g. via the /embed-preview demo route).
 */
@Component({
  selector: "app-embed-preview",
  templateUrl: "./embed-preview.component.html",
  styleUrls: ["./embed-preview.component.scss"],
})
export class EmbedPreviewComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly IMAGE_AUTOPLAY_DELAY_MS = 3000;

  @Input() title = "How to make pizza!";
  @Input() author = "Chef Mario";
  @Input() coverImage =
    "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=1200&q=80";
  @Input() videoUrl: string | null =
    "https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4";
  @Input() duration = "3:15";
  @Input() shareLink = "";
  @Input() description =
    "Learn how to make a delicious homemade pizza from scratch — from preparing the dough to adding your favourite toppings and baking it to golden perfection.";

  @Input() steps: EmbedPreviewStep[] = [
    { title: "Preheat Oven", subtitle: "Heat to 475°F (245°C)", icon: "🍳" },
    { title: "Prepare Dough", subtitle: "Stretch into a 12in round", icon: "🥟" },
    { title: "Add Sauce", subtitle: "Spread tomato base evenly", icon: "🥫" },
    { title: "Add Cheese", subtitle: "Cover with mozzarella", icon: "🧀" },
    { title: "Add Toppings", subtitle: "Olives, peppers, mushrooms", icon: "🫒" },
    { title: "Bake", subtitle: "12–15 minutes until golden", icon: "🔥" },
    { title: "Slice & Serve", subtitle: "Cut into 8 slices", icon: "🍕" },
    { title: "Garnish", subtitle: "Fresh basil on top", icon: "🌿" },
    { title: "Plate Up", subtitle: "Serve hot", icon: "🍽️" },
    { title: "Store Leftovers", subtitle: "Refrigerate within 2 hours", icon: "❄️" },
    { title: "Clean Up", subtitle: "Wash tools and surfaces", icon: "🧽" },
  ];

  @Input() items: EmbedPreviewItem[] = [
    { name: "Pizza dough", detail: "500g", icon: "🥟" },
    { name: "Tomato sauce", detail: "200ml", icon: "🥫" },
    { name: "Mozzarella", detail: "300g", icon: "🧀" },
    { name: "Black olives", detail: "1 cup", icon: "🫒" },
    { name: "Bell peppers", detail: "2", icon: "🫑" },
    { name: "Mushrooms", detail: "150g", icon: "🍄" },
  ];

  /**
   * Optional real flow/bundle object. When provided, its fields are mapped
   * onto the inputs above so the preview reflects actual content instead of
   * the built-in demo data.
   */
  @Input() flowData: any = null;

  @ViewChild("flowTitleEl") flowTitleEl?: ElementRef<HTMLElement>;
  @ViewChild("videoEl") videoEl?: ElementRef<HTMLVideoElement>;

  /** Which selection panel is open over the video, if any. */
  panelOpen: "steps" | "items" | null = null;
  isPlaying = false;
  isAutoplayEnabled = false;
  showDescription = true;
  /** -1 = no step selected (cover shown in main area). */
  activeStepIndex = -1;
  copied = false;
  titleExpanded = false;
  isTitleTruncated = false;

  private swipeStartX = 0;
  private swipeStartY = 0;
  private swipeEndX = 0;
  private swipeEndY = 0;
  private shouldIgnoreSwipe = false;
  private readonly swipeDistanceThreshold = 40;
  private readonly swipeVerticalAllowance = 60;
  private titleResizeObserver?: ResizeObserver;
  private autoplayImageTimeout?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    if (this.flowData) {
      this.applyFlowData(this.flowData);
    }

    if (!this.shareLink && typeof window !== "undefined") {
      this.shareLink = window.location?.href ?? "";
    }
  }

  ngAfterViewInit(): void {
    this.observeTitleTruncation();
  }

  ngOnDestroy(): void {
    this.titleResizeObserver?.disconnect();
    this.clearAutoplayImageTimeout();
  }

  onFlowTitleClick(): void {
    if (!this.isTitleTruncated && !this.titleExpanded) {
      return;
    }
    this.titleExpanded = !this.titleExpanded;
    if (!this.titleExpanded) {
      setTimeout(() => this.updateTitleTruncation(), 0);
    }
  }

  private observeTitleTruncation(): void {
    const el = this.flowTitleEl?.nativeElement;
    if (!el) {
      setTimeout(() => this.observeTitleTruncation(), 0);
      return;
    }

    if (typeof ResizeObserver !== "undefined") {
      this.titleResizeObserver?.disconnect();
      this.titleResizeObserver = new ResizeObserver(() =>
        this.updateTitleTruncation()
      );
      this.titleResizeObserver.observe(el);
    }

    this.updateTitleTruncation();
  }

  private updateTitleTruncation(): void {
    const el = this.flowTitleEl?.nativeElement;
    if (!el || this.titleExpanded) {
      return;
    }
    this.isTitleTruncated = el.scrollWidth > el.clientWidth + 1;
  }

  private applyFlowData(data: any): void {
    if (data.title) {
      this.title = data.title;
    }

    const rawAuthor = data.userName || data.ownerName || data.createdBy || "";
    if (rawAuthor) {
      this.author = String(rawAuthor).replace(/^@/, "");
    }

    const url: string = data.url || data.mediaUrl || "";
    const isVideo =
      data.showImage === false ||
      /\.(mp4|webm|mov|m4v|ogg)(\?|#|$)/i.test(url);

    if (isVideo) {
      this.videoUrl = url || this.videoUrl;
      this.coverImage =
        data.videoThumbNailUrl || data.posterUrl || this.coverImage;
    } else {
      // Image cover: clear any (demo) video so no play controls are shown.
      this.videoUrl = null;
      if (url) {
        this.coverImage = url;
      } else if (data.videoThumbNailUrl) {
        this.coverImage = data.videoThumbNailUrl;
      }
    }

    const steps: any[] = data.steps || data.dataSourceSteps || [];
    if (Array.isArray(steps) && steps.length) {
      this.steps = steps.map((step, index) => {
        const stepUrl: string = step.url || step.mediaUrl || step.imageUrl || "";
        const stepIsVideo =
          step.showImage === false ||
          /\.(mp4|webm|mov|m4v|ogg)(\?|#|$)/i.test(stepUrl);
        const rawDescription =
          step.description || step.stepDescription || step.desc || "";
        return {
          title: step.title || step.stepTitle || `Step ${index + 1}`,
          subtitle: this.stripHtml(rawDescription),
          description: rawDescription,
          icon: "📝",
          image: stepIsVideo
            ? step.videoThumbNailUrl || step.posterUrl || ""
            : stepUrl,
          video: stepIsVideo ? stepUrl : null,
        };
      });
    }

    const items: any[] =
      data.dataSource || data.components || data.itemsRequired || [];
    if (Array.isArray(items) && items.length) {
      this.items = items.map((item) => ({
        name:
          item.itemName || item.itemkey || item.itemKey || item.name || "Item",
        detail: item.itemValue || item.itemvalue || item.detail || "",
        icon: "📦",
      }));
    }

    const flowDescription =
      data.description || data.flowDescription || data.summary || "";
    if (flowDescription) {
      this.description = flowDescription;
    }

    if (data.duration && data.durationDisplay) {
      this.duration = data.durationDisplay;
    }

    this.titleExpanded = false;
    setTimeout(() => this.updateTitleTruncation(), 0);
  }

  private stripHtml(value: string): string {
    if (!value) {
      return "";
    }
    return value.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
  }

  get sidebarCount(): number {
    return this.panelOpen === "items" ? this.items.length : this.steps.length;
  }

  /** Currently selected step, or null when the cover is shown. */
  get selectedStep(): EmbedPreviewStep | null {
    return this.activeStepIndex >= 0 && this.activeStepIndex < this.steps.length
      ? this.steps[this.activeStepIndex]
      : null;
  }

  /** Media shown in the main canvas (selected step, else the cover). */
  get displayImage(): string {
    return this.selectedStep?.image || this.coverImage;
  }

  get displayVideoUrl(): string | null {
    return this.selectedStep ? this.selectedStep.video || null : this.videoUrl;
  }

  get displayTitle(): string {
    return this.selectedStep?.title || this.title;
  }

  /** Step title shown beneath the flow title (only when a step is selected). */
  get selectionLabel(): string {
    if (!this.selectedStep) {
      return "";
    }
    const title = (this.selectedStep.title || "").trim();
    // Avoid duplicating the prefix when the title already starts with "Step".
    return /^step\b/i.test(title)
      ? title
      : `Step ${this.activeStepIndex + 1}: ${title}`;
  }

  /** Description of the current selection (step, else flow/cover). */
  get currentDescription(): string {
    return this.selectedStep
      ? this.selectedStep.description || this.selectedStep.subtitle || ""
      : this.description;
  }

  get canPrev(): boolean {
    return this.activeStepIndex > -1;
  }

  get canNext(): boolean {
    return this.activeStepIndex < this.steps.length - 1;
  }

  togglePanel(panel: "steps" | "items"): void {
    this.panelOpen = this.panelOpen === panel ? null : panel;
  }

  closePanel(): void {
    this.panelOpen = null;
  }

  toggleAutoplay(): void {
    this.isAutoplayEnabled = !this.isAutoplayEnabled;
    if (this.isAutoplayEnabled) {
      this.startAutoplayForCurrentMedia();
    } else {
      this.clearAutoplayImageTimeout();
    }
  }

  onAutoplayClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleAutoplay();
    const target = event.currentTarget as HTMLElement | null;
    target?.blur();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  onAutoplayMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  toggleDescription(): void {
    this.showDescription = !this.showDescription;
  }

  play(): void {
    this.clearAutoplayImageTimeout();
    this.isPlaying = true;
    this.queueVideoPlayback(true);
  }

  selectStep(index: number): void {
    this.clearAutoplayImageTimeout();
    this.activeStepIndex = index;
    this.isPlaying = !!this.displayVideoUrl;
    if (this.isPlaying) {
      this.queueVideoPlayback(true);
    } else if (this.isAutoplayEnabled) {
      this.scheduleAutoplayAdvance();
    }
  }

  prev(): void {
    if (this.canPrev) {
      this.selectStep(this.activeStepIndex - 1);
    }
  }

  next(): void {
    if (this.canNext) {
      this.selectStep(this.activeStepIndex + 1);
    }
  }

  private startAutoplayForCurrentMedia(): void {
    this.clearAutoplayImageTimeout();

    if (this.displayVideoUrl) {
      this.isPlaying = true;
      this.queueVideoPlayback(true);
      return;
    }

    this.isPlaying = false;
    this.scheduleAutoplayAdvance();
  }

  onSwipeStart(event: TouchEvent | MouseEvent): void {
    if (this.panelOpen) {
      this.shouldIgnoreSwipe = true;
      return;
    }

    const point = this.getSwipePoint(event, true);
    if (!point) {
      return;
    }

    this.swipeStartX = point.x;
    this.swipeStartY = point.y;
    this.swipeEndX = point.x;
    this.swipeEndY = point.y;

    const target = event.target as HTMLElement | null;
    this.shouldIgnoreSwipe = this.shouldIgnoreSwipeTarget(target);
  }

  onSwipeEnd(event: TouchEvent | MouseEvent): void {
    if (this.shouldIgnoreSwipe || this.panelOpen) {
      this.resetSwipeTracking();
      return;
    }

    const point = this.getSwipePoint(event, false);
    if (!point) {
      this.resetSwipeTracking();
      return;
    }

    this.swipeEndX = point.x;
    this.swipeEndY = point.y;
    this.handleSwipe();
  }

  private getSwipePoint(
    event: TouchEvent | MouseEvent,
    isStart: boolean
  ): { x: number; y: number } | null {
    const touchEvent = event as TouchEvent;
    if ("touches" in touchEvent || "changedTouches" in touchEvent) {
      const touch = isStart
        ? touchEvent.touches?.[0] || touchEvent.changedTouches?.[0]
        : touchEvent.changedTouches?.[0] || touchEvent.touches?.[0];
      if (!touch) {
        return null;
      }
      return { x: touch.clientX, y: touch.clientY };
    }

    const mouseEvent = event as MouseEvent;
    if ("clientX" in mouseEvent && "clientY" in mouseEvent) {
      return { x: mouseEvent.clientX, y: mouseEvent.clientY };
    }

    return null;
  }

  private shouldIgnoreSwipeTarget(target: HTMLElement | null): boolean {
    if (!target) {
      return false;
    }

    return !!target.closest(
      '[data-noswipe="true"], button, a, input, textarea, select, [role="button"], label.epv-autoplay, .epv-autoplay'
    );
  }

  private handleSwipe(): void {
    const deltaX = this.swipeEndX - this.swipeStartX;
    const deltaY = this.swipeEndY - this.swipeStartY;

    if (
      Math.abs(deltaX) <= Math.abs(deltaY) ||
      Math.abs(deltaX) < this.swipeDistanceThreshold ||
      Math.abs(deltaY) > this.swipeVerticalAllowance
    ) {
      this.resetSwipeTracking();
      return;
    }

    // Swipe left → next step; swipe right → previous step
    if (deltaX < 0) {
      this.next();
    } else {
      this.prev();
    }

    this.resetSwipeTracking();
  }

  private resetSwipeTracking(): void {
    this.swipeStartX = 0;
    this.swipeStartY = 0;
    this.swipeEndX = 0;
    this.swipeEndY = 0;
    this.shouldIgnoreSwipe = false;
  }

  private queueVideoPlayback(shouldAutoplay: boolean): void {
    setTimeout(() => this.syncVideoPlayback(shouldAutoplay), 0);
  }

  private scheduleAutoplayAdvance(): void {
    if (!this.isAutoplayEnabled || this.displayVideoUrl || !this.canNext) {
      return;
    }

    this.autoplayImageTimeout = setTimeout(() => {
      this.autoplayImageTimeout = undefined;
      if (!this.isAutoplayEnabled || this.displayVideoUrl || !this.canNext) {
        return;
      }
      this.selectStep(this.activeStepIndex + 1);
    }, EmbedPreviewComponent.IMAGE_AUTOPLAY_DELAY_MS);
  }

  private clearAutoplayImageTimeout(): void {
    if (this.autoplayImageTimeout) {
      clearTimeout(this.autoplayImageTimeout);
      this.autoplayImageTimeout = undefined;
    }
  }

  private syncVideoPlayback(shouldAutoplay: boolean): void {
    const video = this.videoEl?.nativeElement;
    if (!video) {
      return;
    }

    if (!this.isPlaying) {
      video.pause();
      return;
    }

    if (shouldAutoplay) {
      // Muting before play keeps autoplay working reliably across browsers.
      video.muted = true;
    }

    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        this.isPlaying = false;
      });
    }
  }

  /** Auto-advance to the next step when the current one finishes. */
  onMediaEnded(): void {
    if (!this.isAutoplayEnabled) {
      return;
    }
    if (this.canNext) {
      this.selectStep(this.activeStepIndex + 1);
    }
  }

  copyLink(): void {
    const link = this.shareLink;
    if (!link) {
      return;
    }

    const markCopied = () => {
      this.copied = true;
      setTimeout(() => (this.copied = false), 1800);
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(markCopied).catch(() => {});
    } else {
      const el = document.createElement("textarea");
      el.value = link;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand("copy");
        markCopied();
      } finally {
        document.body.removeChild(el);
      }
    }
  }
}
